import loopback from '@/services/loopback'
import onboardingService from '@/services/onboarding'
import router from '@/router'
import { initClarity } from '@/services/clarity'

export function syncToken ({commit}) {
  if (loopback.token) {
    commit('setAccessToken', loopback.token)
  }
  return Promise.resolve()
}

function clearLocalStorage (commit) {
  commit('setAccessToken', null)
  commit('setUser', null)
  commit('setOrganization', null)
  commit('setSoftswitchIntegration', null)
  commit('setFinancialData', null)
  commit('setOnboarding', null)
  commit('clearImpersonation')
  loopback.removeToken()
  loopback.token = null
}

function applySubscriptionPricingRedirect (state, to, next) {
  if (to.name !== 'subscription' && to.name !== 'pricing') return next()
  const org = state.organization
  if (!org) return next()
  if (org.isUnlimited === true && to.name === 'subscription') return next({ name: 'pricing' })
  if (org.isUnlimited === false && to.name === 'pricing') return next({ name: 'subscription' })
  return next()
}

function applyOnboardingRedirect (state, to, next) {
  const user = state.user
  const org = state.organization
  const onboarding = state.onboarding
  if (!user || user.role !== 'ADMIN') return false
  const isFounder = !!(user.email && org && user.email === org.email)
  if (!isFounder) return false
  if (!onboarding) return false
  const needsOnboarding = !onboarding.onboardingCompletedAt
  if (needsOnboarding && to.name !== 'onboarding') {
    next({ name: 'onboarding' })
    return true
  }
  if (!needsOnboarding && to.name === 'onboarding') {
    next({ name: 'home' })
    return true
  }
  return false
}

function applyHomeAccessRedirect (state, to, next) {
  if (to.name !== 'home') return false
  const user = state.user
  const org = state.organization
  if (!user || !org) return false
  const isFounder = user.role === 'ADMIN' && user.email && user.email === org.email
  if (isFounder) return false
  next({ name: 'dashboard' })
  return true
}

function evaluateRoute (state, commit, to, next) {
  return (sessionError) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
      // this route requires auth, check if logged in
      // if not, redirect to login page (except when it's profile route and
      // there is an access_token).
      if (to.name === 'change-password' && to.query.access_token) {
        next()
      } else if (!state.accessToken) {
        next({
          name: 'login',
          params: { sessionError },
          query: { redirect: to.fullPath }
        })
      } else {
        loopback.head('/users/accessTokens').then(() => {
          if (applyHomeAccessRedirect(state, to, next)) return
          if (applyOnboardingRedirect(state, to, next)) return
          applySubscriptionPricingRedirect(state, to, next)
        }).catch((err) => {
          if (err && err.response && err.response.status === 401) {
            clearLocalStorage(commit)
            next({
              name: 'login',
              query: {
                accessTokenExpired: true,
                redirect: to.fullPath
              }
            })
          } else {
            if (applyOnboardingRedirect(state, to, next)) return
            applySubscriptionPricingRedirect(state, to, next)
          }
        })
      }
    } else {
      next() // make sure to always call next()!
    }
  }
}

export function syncRouter ({state, dispatch, commit}, myRouter) {
  myRouter.beforeEach((to, _from, next) => {
    dispatch('syncToken').then(
      evaluateRoute(state, commit, to, next)
    )
  })
}

export function signIn ({commit, dispatch, state}, {email, password, recaptchaToken}) {
  return loopback
    .post('/users/login', {
      email,
      password,
      recaptchaToken
    })
    .then((token) => {
      commit('setAccessToken', token)
      if (state.accessToken !== null) {
        loopback.setToken(state.accessToken)
        return dispatch('loadUserInfos')
      } else {
        loopback.removeToken()
      }
    })
}

export function signOut ({commit, state}) {
  return loopback
    .post('/users/logout', {
      access_token: state.accessToken.id
    })
    .then(_ => {
      clearLocalStorage(commit)
      return router.push({name: 'login'})
    })
}

export function loadUserInfos ({commit}) {
  return new Promise(async (resolve, reject) => {
    const user = await loopback.get(`/users/me`).catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
      return reject(err)
    })
    if (user) {
      commit('setUser', user)
      initClarity(user.email)
    }
    const organization = await loopback.get(`/accounts`).catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
      return reject(err)
    })
    if (organization) commit('setOrganization', organization)

    await loadSoftswitchIntegration({commit}, 'softswitch')

    if (user && user.role === 'ADMIN') {
      const financialData = await loopback.get('/accounts/financial').catch((err) => {
        if (err.statusCode !== 429) loopback.removeToken()
        return reject(err)
      })
      commit('setFinancialData', financialData)
    } else {
      commit('setFinancialData', null)
    }

    if (user) {
      try {
        const onboarding = await onboardingService.getMyOnboarding()
        if (onboarding) commit('setOnboarding', onboarding)
      } catch (err) {
        commit('setOnboarding', null)
      }
    }
    return resolve()
  })
}

export function startImpersonation ({state, commit, dispatch}, {accessToken, client, impersonationId, expiratedAt}) {
  const originalToken = state.accessToken
  const originalUser = state.user
  commit('setImpersonation', {
    active: true,
    client,
    impersonationId,
    expiratedAt,
    startedAt: Date.now(),
    originalToken,
    originalUser
  })
  commit('setAccessToken', accessToken)
  loopback.setToken(accessToken)
  return dispatch('loadUserInfos')
}

export async function stopImpersonation ({state, commit, dispatch}, payload) {
  const impersonation = state.impersonation
  if (impersonation === null) return
  try {
    await loopback.post(`/account-impersonations/${impersonation.impersonationId}/end`)
  } catch (error) {
    console.error('api4com > error ending impersonation:', error)
  }
  commit('setAccessToken', impersonation.originalToken)
  loopback.setToken(impersonation.originalToken)
  commit('setUser', impersonation.originalUser)
  commit('clearImpersonation')
  if (payload !== undefined && payload.followedByLogout === true) return
  await dispatch('loadUserInfos')
  return router.push({name: 'dashboard'})
}

export async function reloadOnboarding ({commit}) {
  try {
    const onboarding = await onboardingService.getMyOnboarding()
    commit('setOnboarding', onboarding || null)
  } catch (err) {
    commit('setOnboarding', null)
  }
}

export function loadSoftswitchIntegration ({commit}, gateway) {
  return loopback
    .get(`/organization_integrations?filter[where][gateway]=${gateway}`)
    .then((integrations) => {
      if (integrations.length === 0) return commit('setSoftswitchIntegration', null)
      const {metadata} = integrations.pop()
      commit('setSoftswitchIntegration', {
        metadata: {
          hasDynamicTariff: metadata.hasDynamicTariff,
          receivedBonusCredit: metadata.receivedBonusCredit
        }
      })
    })
    .catch((err) => {
      if (err.statusCode !== 429) loopback.removeToken()
    })
}

export function changePassword (ctx, {oldPassword, newPassword}) {
  return loopback
    .post(
      '/users/change-password',
      {oldPassword, newPassword}
    )
}

export function resetPassword (ctx, {newPassword, accessToken}) {
  return loopback
    .post(
      `/users/reset-password?access_token=${accessToken}`,
      {newPassword}
    )
}

export function rememberPassword (ctx, {email, recaptchaToken}) {
  return loopback
    .post('/users/reset', {email, recaptchaToken})
}

export function signUp (ctx, {name, organizationName, password, email, phone, userQuantity, voucherCode, redirect, campaignId, attribution, recaptchaToken}) {
  return loopback
    .post('/accounts/signup', {
      'name': name,
      'organization_name': organizationName,
      'password': password,
      'email': email,
      'phone': phone,
      'user_quantity': String(userQuantity),
      'voucher': voucherCode,
      'redirect': redirect,
      'campaign_id': campaignId,
      'recaptchaToken': recaptchaToken,
      ...attribution
    })
    .catch((err) => {
      return Promise.reject(err)
    })
}



// WEBPACK FOOTER //
// ./src/store/modules/auth/actions.js